// useAttachments — Polish #18 composer attachment hooks.
//
// Outbound flow:
//   1. Composer calls uploadAttachment.mutate(file) when Kimi picks or drops
//      a file. The hook reads the file as base64 in the browser and POSTs to
//      /api/conversations/:id/attachments.  Returns the attachment row with
//      a 1-hour signed URL for the thumbnail.
//   2. The resolved row is added to local Composer state (staged chips).
//   3. When Kimi hits Send, the Composer passes the staged attachment ids to
//      /api/conversations/:id/reply.  The backend binds them to the outbound
//      message row after the Gmail send succeeds.
//
// Unstage flow:
//   deleteAttachment.mutate(id) removes a staged row (message_id is null).
//   Backend refuses with 409 already_sent if we try to delete a bound row.
//
// The backend also exposes GET /api/conversations/:id/attachments which lists
// every attachment on the thread (inbound and outbound).  We don't call that
// from here today — thread view attachments come through the main GET
// conversation payload.  The listing endpoint is exposed for future use.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost, apiDelete } from '../api';

export interface StagedAttachment {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  url: string | null;
  created_at: string;
}

interface UploadResponse extends StagedAttachment {}

/**
 * Read a File as a raw base64 string (no data URL prefix).  We use
 * FileReader.readAsDataURL and strip the `data:mime;base64,` prefix so the
 * backend can Buffer.from(..., 'base64') straight through.
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const out = reader.result;
      if (typeof out !== 'string') {
        reject(new Error('unexpected_reader_output'));
        return;
      }
      const idx = out.indexOf(',');
      resolve(idx >= 0 ? out.slice(idx + 1) : out);
    };
    reader.onerror = () => reject(reader.error ?? new Error('read_failed'));
    reader.readAsDataURL(file);
  });
}

export function useUploadAttachment(conversationId: string | undefined) {
  return useMutation<UploadResponse, Error, File>({
    mutationFn: async (file: File) => {
      const data_base64 = await fileToBase64(file);
      return apiPost<UploadResponse, {
        filename: string;
        mime_type: string;
        data_base64: string;
      }>(`/api/conversations/${conversationId}/attachments`, {
        filename: file.name,
        mime_type: file.type || 'application/octet-stream',
        data_base64,
      });
    },
  });
}

export function useDeleteAttachment() {
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (attachmentId: string) =>
      apiDelete<{ ok: true }>(`/api/attachments/${attachmentId}`),
  });
}

// ─── Proof tracker hooks ──────────────────────────────────────────────────
// These mirror the CRUD routes in packages/backend/src/routes/proofs.ts.
// ProofTrackerPanel is the only consumer today.

export interface ProofRow {
  id: string;
  version: number;
  status: 'sent' | 'awaiting' | 'approved' | 'rejected' | 'superseded';
  sent_at: string;
  status_changed_at: string;
  notes: string | null;
  nudge_48h_sent_at: string | null;
  nudge_96h_sent_at: string | null;
  superseded_by: string | null;
  attachment: {
    id: string;
    filename: string;
    mime_type: string;
    size_bytes: number;
    url: string | null;
  } | null;
}

export interface ProofsResponse {
  proofs: ProofRow[];
}

import { useQuery } from '@tanstack/react-query';
import { apiGet, apiPatch } from '../api';

export function useProofs(conversationId: string | undefined) {
  return useQuery<ProofsResponse>({
    queryKey: ['proofs', conversationId],
    queryFn: () => apiGet<ProofsResponse>(`/api/conversations/${conversationId}/proofs`),
    enabled: !!conversationId,
    staleTime: 10_000,
  });
}

export function useMarkProof(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { proof: { id: string } },
    Error,
    { attachmentId: string; notes?: string }
  >({
    mutationFn: (vars) =>
      apiPost<{ proof: { id: string } }, { attachment_id: string; notes?: string }>(
        `/api/conversations/${conversationId}/proofs`,
        { attachment_id: vars.attachmentId, notes: vars.notes },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['proofs', conversationId] });
    },
  });
}

export function useUpdateProof(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { proof: { id: string; status: string } },
    Error,
    {
      proofId: string;
      status?: 'sent' | 'awaiting' | 'approved' | 'rejected';
      notes?: string | null;
    }
  >({
    mutationFn: (vars) =>
      apiPatch<
        { proof: { id: string; status: string } },
        { status?: string; notes?: string | null }
      >(`/api/proofs/${vars.proofId}`, {
        status: vars.status,
        notes: vars.notes,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['proofs', conversationId] });
    },
  });
}

export function useDeleteProof(conversationId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (proofId: string) =>
      apiDelete<{ ok: true }>(`/api/proofs/${proofId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['proofs', conversationId] });
    },
  });
}
